export interface BlogPathItem {
  id: string
  name: string
  slug: string
}

/** A managed category attached to a post (with its ancestor path). */
export interface BlogCategoryRef {
  id: string
  name: string
  slug: string
  parentId: string | null
  path: BlogPathItem[]
}

/** A node in the hierarchical category/tag tree returned by the public API. */
export interface BlogTaxonomy {
  id: string
  name: string
  slug: string
  parentId: string | null
  /** Direct post count plus every descendant's count. */
  count: number
  path: BlogPathItem[]
  children: BlogTaxonomy[]
}

export interface BlogPost {
  /** URL segment used by the `/blog/:slug` route */
  slug: string
  title: string
  /** Short blurb shown on the blog listing cards */
  excerpt: string
  /** Listing card image */
  image: string
  date: string
  author: string
  /** Primary category name (first managed category). */
  category: string
  /** All managed categories on the post. */
  categories?: BlogCategoryRef[]
  tags: string[]
  /** Opening paragraph on the detail page */
  intro: string
  /** Rich HTML body authored in the dashboard WYSIWYG editor */
  content?: string
  /** Optional detail-page gallery images (API-provided) */
  gallery?: string[]
  /** SEO metadata authored in the dashboard post screen */
  metaTitle?: string
  metaDescription?: string
  metaKeywords?: string
  ogImage?: string
  canonicalUrl?: string
  noIndex?: boolean
}

interface RawTaxonomy {
  id: string
  name: string
  slug: string
  parentId: string | null
}

/** Build a nested taxonomy tree from a flat list, rolling up post counts. */
function buildTree(raw: RawTaxonomy[], counts: Map<string, number> = new Map()): BlogTaxonomy[] {
  const byId = new Map<string, BlogTaxonomy>()
  for (const node of raw) {
    byId.set(node.id, { ...node, count: counts.get(node.id) ?? 0, path: [], children: [] })
  }

  const roots: BlogTaxonomy[] = []
  for (const node of raw) {
    const treeNode = byId.get(node.id)!
    const parent = node.parentId ? byId.get(node.parentId) : undefined
    if (parent) parent.children.push(treeNode)
    else roots.push(treeNode)
  }

  const visit = (node: BlogTaxonomy, ancestors: BlogPathItem[]) => {
    node.path = [...ancestors, { id: node.id, name: node.name, slug: node.slug }]
    let total = node.count
    for (const child of node.children) {
      visit(child, node.path)
      total += child.count
    }
    node.count = total
  }

  for (const root of roots) visit(root, [])
  return roots
}

/** Offline mirror of the database category tree (see `website.seed.ts`). */
const RAW_BLOG_CATEGORIES: RawTaxonomy[] = [
  { id: 'cat-health', name: 'Health & Wellness', slug: 'health-wellness', parentId: null },
  { id: 'cat-preventive', name: 'Preventive Care', slug: 'preventive-care', parentId: 'cat-health' },
  { id: 'cat-nutrition', name: 'Nutrition', slug: 'nutrition', parentId: 'cat-health' },
  { id: 'cat-emergency', name: 'Emergency Care', slug: 'emergency-care', parentId: null },
  { id: 'cat-first-aid', name: 'First Aid', slug: 'first-aid', parentId: 'cat-emergency' },
  { id: 'cat-child', name: 'Child Health', slug: 'child-health', parentId: null },
  { id: 'cat-heart', name: 'Heart Health', slug: 'heart-health', parentId: null },
  { id: 'cat-chronic', name: 'Chronic Conditions', slug: 'chronic-conditions', parentId: null },
  { id: 'cat-diabetes', name: 'Diabetes', slug: 'diabetes', parentId: 'cat-chronic' },
  { id: 'cat-diagnostics', name: 'Diagnostics', slug: 'diagnostics', parentId: null },
]

/** Offline mirror of the database tag tree. */
const RAW_BLOG_TAGS: RawTaxonomy[] = [
  { id: 'tag-emergency', name: 'Emergency', slug: 'emergency', parentId: null },
  { id: 'tag-first-aid', name: 'First Aid', slug: 'first-aid', parentId: null },
  { id: 'tag-heart', name: 'Heart Health', slug: 'heart-health', parentId: null },
  { id: 'tag-child', name: 'Child Care', slug: 'child-care', parentId: null },
  { id: 'tag-diabetes', name: 'Diabetes', slug: 'diabetes', parentId: null },
  { id: 'tag-prevention', name: 'Prevention', slug: 'prevention', parentId: null },
  { id: 'tag-nutrition', name: 'Nutrition', slug: 'nutrition', parentId: null },
  { id: 'tag-diagnostics', name: 'Diagnostics', slug: 'diagnostics', parentId: null },
  { id: 'tag-general', name: 'General Medicine', slug: 'general-medicine', parentId: null },
  { id: 'tag-health-tips', name: 'Health Tips', slug: 'health-tips', parentId: null },
]

/** Flatten a taxonomy tree into a single list (depth-first). */
export function flattenBlogTree(nodes: BlogTaxonomy[]): BlogTaxonomy[] {
  const out: BlogTaxonomy[] = []
  const walk = (list: BlogTaxonomy[]) => {
    for (const node of list) {
      out.push(node)
      walk(node.children)
    }
  }
  walk(nodes)
  return out
}

/** Filter the bundled demo posts by category/tag slugs and free-text search. */
export interface BlogFilters {
  categories?: string[]
  tags?: string[]
  search?: string
}

export const DETAIL_IMAGE = '/assets/images/blog/blog-details/blog-single-1.jpg'

export const DETAIL_GALLERY: [string, string] = [
  '/assets/images/blog/blog-details/blog-single-gallary-1.jpg',
  '/assets/images/blog/blog-details/blog-single-gallary-2.jpg',
]

/**
 * Single source of truth for the Fast Aid Hospital health blog. The `/blog`
 * listing and the dynamic `/blog/:slug` detail pages are both generated from
 * this list.
 */
export const posts: BlogPost[] = [
  {
    slug: 'emergency-warning-signs',
    title: '10 Emergency Warning Signs You Should Never Ignore',
    excerpt:
      'Chest pain, difficulty breathing, sudden weakness and heavy bleeding need urgent care. Learn the warning signs that mean you should go to the emergency department immediately.',
    image: '/assets/images/blog/blog-1.jpg',
    date: 'March 14, 2025',
    author: 'Fast Aid Hospital',
    category: 'Emergency Care',
    tags: ['Emergency', 'First Aid'],
    intro:
      'Some symptoms are the body’s way of signalling a life-threatening emergency. Recognising them early and reaching a hospital quickly can save a life — often within the first “golden hour” of an illness or injury.',
    metaTitle: '10 Emergency Warning Signs You Should Never Ignore | Fast Aid Hospital',
    metaDescription:
      'Know the emergency warning signs — chest pain, breathing difficulty, stroke symptoms, heavy bleeding and more — and when to go to Fast Aid Hospital, Chauddagram.',
    metaKeywords:
      'emergency warning signs, when to go to hospital, chest pain, stroke symptoms, emergency care Chauddagram',
    content: `
<p>Not every health problem needs an emergency visit, but some cannot wait. At Fast Aid Hospital, Chauddagram, our emergency department is open 24 hours a day because the first minutes after a serious illness or accident often decide the outcome.</p>

<h3>Warning signs that need immediate emergency care</h3>
<ul>
  <li><strong>Chest pain or pressure</strong> that lasts more than a few minutes, spreads to the arm or jaw, or comes with sweating and nausea.</li>
  <li><strong>Difficulty breathing</strong> or a feeling of choking, especially if it starts suddenly.</li>
  <li><strong>Signs of stroke</strong> — sudden weakness or drooping on one side of the face, slurred speech, or loss of vision in one eye.</li>
  <li><strong>Severe bleeding</strong> that does not stop after ten minutes of firm pressure.</li>
  <li><strong>Loss of consciousness</strong>, fainting, or a seizure that does not stop.</li>
  <li><strong>Severe abdominal pain</strong> that is sudden, constant or makes the stomach hard.</li>
  <li><strong>High fever with confusion or a stiff neck</strong>, which can point to a serious infection.</li>
  <li><strong>Serious injury</strong> after a road accident, fall or deep cut.</li>
  <li><strong>Poisoning or a drug overdose</strong>, including snake bite and pesticide exposure.</li>
  <li><strong>Sudden, severe headache</strong> — often described as the worst headache of your life.</li>
</ul>

<h3>What to do while you are on the way</h3>
<p>Call our emergency hotline on <strong>09610009620</strong> so the team can prepare for your arrival. Keep the patient calm and lying down, do not give food or drink if they are unconscious, and never try to drive yourself if you feel unwell.</p>

<h3>Why the “golden hour” matters</h3>
<p>In emergencies such as heart attack, stroke and severe trauma, treatment given within the first hour greatly improves the chance of survival and recovery. That is why Fast Aid Hospital keeps emergency physicians, nursing staff, a resuscitation bay, laboratory, X-ray and ECG available around the clock.</p>

<h3>Prevention is still the best medicine</h3>
<p>Regular checkups help detect high blood pressure, diabetes and heart disease before they become emergencies. If you have a chronic condition, keep your medicines up to date and attend your follow-up visits.</p>
`,
  },
  {
    slug: 'diabetes-blood-pressure-guide',
    title: 'Managing Diabetes and High Blood Pressure: A Practical Guide',
    excerpt:
      'Two of the most common long-term conditions in Bangladesh can be controlled with the right routine. Here is a simple, practical guide to medicines, diet, monitoring and follow-up.',
    image: '/assets/images/blog/blog-2.jpg',
    date: 'March 15, 2025',
    author: 'Fast Aid Hospital',
    category: 'Chronic Conditions',
    tags: ['Diabetes', 'Prevention'],
    intro:
      'Diabetes and high blood pressure often go together, and both quietly damage the heart, kidneys, eyes and nerves when they are not controlled. The good news is that with regular monitoring and a few daily habits, most people can live a full and active life.',
    metaTitle: 'Managing Diabetes & High Blood Pressure | Fast Aid Hospital',
    metaDescription:
      'A practical guide to controlling diabetes and high blood pressure through medicines, diet, exercise, home monitoring and regular checkups at Fast Aid Hospital, Chauddagram.',
    metaKeywords:
      'diabetes management, high blood pressure, blood sugar control, hypertension diet, chronic disease Bangladesh',
    content: `
<p>Diabetes and high blood pressure are among the most common health problems we see at Fast Aid Hospital, Chauddagram. Many patients feel completely well while their blood sugar and pressure are already too high, which is why routine screening is so important.</p>

<h3>Know your numbers</h3>
<p>Ask your doctor what your target blood sugar and blood pressure should be. A simple home notebook — or a note on your phone — with dates and readings helps your physician adjust treatment accurately at each visit.</p>
<ul>
  <li>Check fasting blood sugar as advised, usually every 1–3 months.</li>
  <li>Measure blood pressure at rest, ideally at the same time each day.</li>
  <li>Get an annual eye, kidney and foot check.</li>
</ul>

<h3>Take your medicines regularly</h3>
<p>Blood pressure and diabetes medicines work only when they are taken consistently. Do not stop or change a dose because you feel better. If a medicine causes side effects, speak to your doctor rather than stopping it yourself.</p>

<h3>Eat for steady blood sugar</h3>
<ul>
  <li>Choose whole grains such as brown rice, oats and whole-wheat roti over refined flour.</li>
  <li>Fill half your plate with vegetables and a quarter with protein (fish, chicken, lentils or beans).</li>
  <li>Limit sugary drinks, sweets and deep-fried snacks.</li>
  <li>Reduce salt and avoid adding extra salt at the table.</li>
</ul>

<h3>Move every day</h3>
<p>At least 30 minutes of brisk walking, five days a week, can lower both blood sugar and blood pressure. Any activity is better than none — start slowly and build up.</p>

<h3>Do not ignore follow-up</h3>
<p>Regular review at Fast Aid Hospital lets us check your reports, adjust medicine and catch complications early. Bring your home readings, all your medicines and any previous reports to every visit.</p>
`,
  },
  {
    slug: 'child-fever-when-to-see-doctor',
    title: 'Child Fever: When to Treat at Home and When to See a Doctor',
    excerpt:
      'Fever is common in children, but some signs need urgent attention. Learn how to care for your child at home and the red flags that mean you should come to the hospital.',
    image: '/assets/images/blog/blog-3.jpg',
    date: 'March 16, 2025',
    author: 'Fast Aid Hospital',
    category: 'Child Health',
    tags: ['Child Care', 'Prevention'],
    intro:
      'A fever is the body’s natural response to infection and is very common in young children. Most fevers are not dangerous, but a few warning signs mean your child needs to be seen by a doctor without delay.',
    metaTitle: 'Child Fever: When to Treat at Home & When to See a Doctor | Fast Aid Hospital',
    metaDescription:
      'How to manage a child’s fever at home and the danger signs — in newborns, breathing difficulty and drowsiness — that need urgent care at Fast Aid Hospital, Chauddagram.',
    metaKeywords:
      'child fever, baby fever, when to see doctor for fever, pediatric care Bangladesh, fever in children',
    content: `
<p>Parents often worry most when a child has a fever. At Fast Aid Hospital, our pediatrics team helps families understand what is normal, how to bring the temperature down safely and when to seek help immediately.</p>

<h3>Caring for your child at home</h3>
<ul>
  <li>Keep the child in light clothing and a comfortable, well-ventilated room.</li>
  <li>Offer plenty of fluids — breast milk, water, soup or oral rehydration solution.</li>
  <li>Give paracetamol in the dose your doctor recommends for the child’s weight.</li>
  <li>Sponge with lukewarm water if the child is very hot; never use ice-cold water or alcohol.</li>
  <li>Do not give aspirin to children.</li>
</ul>

<h3>See a doctor if your child has</h3>
<ul>
  <li>A fever in a baby under 3 months old.</li>
  <li>Fever lasting more than 3 days, or fever that returns after improving.</li>
  <li>Difficulty breathing, fast breathing or chest indrawing.</li>
  <li>Unusual drowsiness, confusion or a child who is difficult to wake.</li>
  <li>Refusal to drink, repeated vomiting or signs of dehydration (dry mouth, no urine).</li>
  <li>A rash, stiff neck, severe headache or a fit (convulsion).</li>
  <li>Fever after a recent vaccination with severe swelling or crying that will not stop.</li>
</ul>

<h3>What happens at the hospital</h3>
<p>Our team checks the child’s temperature, breathing, hydration and general condition, and orders tests such as blood count, malaria or dengue screening when needed. Most children can be treated at home with clear instructions, while some need observation or admission.</p>

<h3>Prevention</h3>
<p>Keep your child’s immunisation up to date, teach hand-washing, and avoid close contact with people who have fever and cough. Good nutrition helps children fight infection better.</p>
`,
  },
  {
    slug: 'heart-health-prevention',
    title: 'Heart Health in Bangladesh: Small Habits That Protect Your Heart',
    excerpt:
      'Heart disease is rising in Bangladesh, but much of the risk is preventable. Learn the everyday habits that keep your heart strong and when to get a heart checkup.',
    image: '/assets/images/blog/blog-4.jpg',
    date: 'March 17, 2025',
    author: 'Fast Aid Hospital',
    category: 'Heart Health',
    tags: ['Heart Health', 'Nutrition'],
    intro:
      'Heart attacks and strokes are increasingly common in Bangladesh, and they are happening at younger ages. The reassuring news is that most risk comes from habits we can change — and early screening can catch problems before they become serious.',
    metaTitle: 'Heart Health in Bangladesh: Habits That Protect Your Heart | Fast Aid Hospital',
    metaDescription:
      'Protect your heart with simple daily habits — healthy food, exercise, no smoking and regular heart checkups at Fast Aid Hospital, Chauddagram.',
    metaKeywords:
      'heart health, heart disease prevention, heart checkup Bangladesh, cholesterol, blood pressure, ECG',
    content: `
<p>At Fast Aid Hospital, Chauddagram, our cardiology service sees many patients whose heart problems could have been prevented or delayed. Taking care of your heart does not require drastic changes — it is built from small, consistent habits.</p>

<h3>Know your risk</h3>
<p>High blood pressure, diabetes, high cholesterol, smoking, obesity and a family history of heart disease all increase your risk. A simple heart checkup — including blood pressure, blood sugar, cholesterol and an ECG — gives you a clear starting point.</p>

<h3>Eat for a healthy heart</h3>
<ul>
  <li>Use less oil, salt and sugar in cooking.</li>
  <li>Eat more fish, vegetables, fruit, lentils and whole grains.</li>
  <li>Limit red meat, processed food and deep-fried snacks.</li>
  <li>Avoid tobacco in every form and limit alcohol.</li>
</ul>

<h3>Move more, sit less</h3>
<p>Aim for at least 30 minutes of brisk walking or other moderate activity on most days. Even short walks after meals help control blood sugar and blood pressure.</p>

<h3>Manage stress and sleep</h3>
<p>Long-term stress and poor sleep raise blood pressure and encourage unhealthy habits. Try to sleep 7–8 hours, take short breaks during work and make time for family and rest.</p>

<h3>Warning signs you must not ignore</h3>
<p>Chest pain or pressure, breathlessness, palpitations, unusual tiredness or pain spreading to the arm, neck or jaw may signal a heart problem. Come to the emergency department immediately — do not wait for it to pass.</p>

<h3>When to get a heart checkup</h3>
<p>Adults over 40, anyone with diabetes or high blood pressure, smokers and people with a family history of heart disease should have a heart checkup at least once a year. Our team will assess your risk and build a plan that fits your life.</p>
`,
  },
]

export function getPost(slug: string | undefined): BlogPost | undefined {
  if (!slug) return undefined
  return posts.find((post) => post.slug === slug)
}

function staticCategoryCounts(): Map<string, number> {
  const counts = new Map<string, number>()
  for (const post of posts) {
    const name = post.category?.toLowerCase()
    const node = RAW_BLOG_CATEGORIES.find((category) => category.name.toLowerCase() === name)
    if (node) counts.set(node.id, (counts.get(node.id) ?? 0) + 1)
  }
  return counts
}

function staticTagCounts(): Map<string, number> {
  const counts = new Map<string, number>()
  for (const post of posts) {
    for (const tag of post.tags) {
      const node = RAW_BLOG_TAGS.find((item) => item.name.toLowerCase() === tag.toLowerCase())
      if (node) counts.set(node.id, (counts.get(node.id) ?? 0) + 1)
    }
  }
  return counts
}

/** Offline category tree used when the public API is unreachable. */
export const blogCategoryTree: BlogTaxonomy[] = buildTree(RAW_BLOG_CATEGORIES, staticCategoryCounts())
/** Offline tag tree used when the public API is unreachable. */
export const blogTagTree: BlogTaxonomy[] = buildTree(RAW_BLOG_TAGS, staticTagCounts())

/** Flat category list (kept for consumers that only need a single level). */
export const blogCategories = flattenBlogTree(blogCategoryTree)
/** Flat tag names. */
export const blogTags = flattenBlogTree(blogTagTree).map((tag) => tag.name)

export function findBlogCategoryBySlug(slug: string): BlogTaxonomy | undefined {
  return flattenBlogTree(blogCategoryTree).find((category) => category.slug === slug)
}

/** Slugs → names, including every descendant category. */
function expandStaticCategoryNames(slugs: string[]): Set<string> {
  const flat = flattenBlogTree(blogCategoryTree)
  const childrenByParent = new Map<string, BlogTaxonomy[]>()
  for (const node of flat) {
    if (!node.parentId) continue
    const list = childrenByParent.get(node.parentId) ?? []
    list.push(node)
    childrenByParent.set(node.parentId, list)
  }

  const names = new Set<string>()
  const seen = new Set<string>()
  const queue = flat.filter((node) => slugs.includes(node.slug))
  while (queue.length) {
    const node = queue.shift()!
    if (seen.has(node.id)) continue
    seen.add(node.id)
    names.add(node.name.toLowerCase())
    for (const child of childrenByParent.get(node.id) ?? []) queue.push(child)
  }
  return names
}

/**
 * Filter the bundled posts the same way the public API filters published
 * posts (multi-category/tag slugs, descendants included). Offline fallback.
 */
export function selectPosts(all: BlogPost[], { categories = [], tags = [], search }: BlogFilters = {}): BlogPost[] {
  const query = search?.trim().toLowerCase()
  const categoryNames = expandStaticCategoryNames(categories)
  const tagNames = new Set(
    flattenBlogTree(blogTagTree)
      .filter((tag) => tags.includes(tag.slug))
      .map((tag) => tag.name.toLowerCase()),
  )

  return all.filter((post) => {
    if (categories.length) {
      const postCategory = post.category?.toLowerCase()
      if (!postCategory || !categoryNames.has(postCategory)) return false
    }
    if (tags.length && !post.tags.some((tag) => tagNames.has(tag.toLowerCase()))) return false
    if (query) {
      const haystack =
        `${post.title} ${post.excerpt} ${post.intro} ${post.category} ${post.tags.join(' ')}`.toLowerCase()
      if (!haystack.includes(query)) return false
    }
    return true
  })
}
